import type {
  ApiDifficulty,
  CareerDetailResponse,
  CourseDetailResponse,
  LearnPostResponse,
  LessonResponse,
  ProblemDetailResponse,
} from '@/api/types';
import site from '@/config/site.json';
import { primaryTopic, topicBySlug, topicPath, type TopicMeta } from '@/data/topics';
import {
  COURSE_LEVEL_UZ,
  careerSeoDescription,
  courseSeoDescription,
  learnSeoDescription,
  lessonSeoDescription,
  problemSeoDescription,
} from '@/lib/seoText';

/**
 * schema.org JSON-LD — YAGONA manba. Prerender (worker `<head>`) va brauzer (useSeo
 * effekti) aynan shu funksiyalar natijasini yozadi; har bir tugun alohida
 * `<script type="application/ld+json" data-ld="<@type>">` — sahifada har turdan bittadan.
 * Sof funksiyalar: server va client bir xil natija beradi.
 */

export type JsonLdNode = { '@type': string } & Record<string, unknown>;

export const SITE_URL = site.url.replace(/\/$/, '');
export const SITE_NAME = site.name;
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

/** Premium bo'lim (qulflangan shart) — paywall markup shu selektorga ishora qiladi */
export const PAYWALL_CLASS = 'paywall-section';

export const TOPICS_INDEX_PATH = '/topics';
export const COURSES_INDEX_PATH = '/courses';
export const LEARN_INDEX_PATH = '/learn';

export const coursePath = (slug: string) => `${COURSES_INDEX_PATH}/${slug}`;
export const lessonPath = (courseSlug: string, lessonSlug: string) => `${coursePath(courseSlug)}/${lessonSlug}`;
export const careerPath = (slug: string) => `/careers/${slug}`;
export const learnPath = (slug: string) => `${LEARN_INDEX_PATH}/${slug}`;

export function absoluteUrl(path: string): string {
  return path.startsWith('http') ? path : `${SITE_URL}${path}`;
}

/** Ijtimoiy tarmoqlar (config/site.json) — bo'sh qiymatlar tashlanadi */
export function socialProfiles(): string[] {
  return Object.values(site.social).filter((u): u is string => typeof u === 'string' && u.length > 0);
}

function organization(): JsonLdNode {
  const sameAs = socialProfiles();
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: absoluteUrl(site.logo),
    ...(sameAs.length ? { sameAs } : {}),
  };
}

function website(): JsonLdNode {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: 'uz',
    publisher: { '@id': ORG_ID },
  };
}

export interface Crumb {
  name: string;
  path: string;
}

function breadcrumbList(crumbs: Crumb[]): JsonLdNode {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

const HOME: Crumb = { name: 'Bosh sahifa', path: '/' };
const TOPICS_CRUMB: Crumb = { name: 'Mavzular', path: TOPICS_INDEX_PATH };
const PROBLEMS_CRUMB: Crumb = { name: 'Masalalar', path: '/problems' };
const COURSES_CRUMB: Crumb = { name: 'Kurslar', path: COURSES_INDEX_PATH };
const LEARN_CRUMB: Crumb = { name: "O'rganish", path: LEARN_INDEX_PATH };

/**
 * Masala breadcrumb'i (uz). Asosiy mavzu bo'lsa: Bosh sahifa › Mavzular › {mavzu} › {masala},
 * bo'lmasa: Bosh sahifa › Masalalar › {masala}. Ko'rinadigan breadcrumb ham shu tuzilmada.
 */
export function problemCrumbs(p: { slug: string; title: string; topics: string[] }): Crumb[] {
  const topic = primaryTopic(p.topics);
  const self = { name: p.title, path: `/problems/${p.slug}` };
  return topic
    ? [HOME, TOPICS_CRUMB, { name: topic.title.uz, path: topicPath(topic.slug) }, self]
    : [HOME, PROBLEMS_CRUMB, self];
}

const LEVEL_UZ: Record<ApiDifficulty, string> = { EASY: 'Oson', MEDIUM: "O'rta", HARD: 'Qiyin' };

/** Masala sahifasi: BreadcrumbList + LearningResource */
export function problemStructuredData(p: ProblemDetailResponse): JsonLdNode[] {
  const topic: TopicMeta | undefined = primaryTopic(p.topics);
  const url = absoluteUrl(`/problems/${p.slug}`);
  const resource: JsonLdNode = {
    '@type': 'LearningResource',
    name: p.title,
    description: problemSeoDescription(p),
    url,
    inLanguage: 'uz',
    learningResourceType: 'Exercise',
    educationalLevel: LEVEL_UZ[p.difficulty],
    teaches: topic ? topic.title.uz : 'SQL',
    // Shart ochiq (bepul). Qulflangan premium masalada shart HTML ga umuman chiqmaydi —
    // faqat shunda paywall markup (bo'limning o'zi ko'rinadigan qulf bloki).
    isAccessibleForFree: !p.locked,
    ...(p.locked
      ? {
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: false,
            cssSelector: `.${PAYWALL_CLASS}`,
          },
        }
      : {}),
    provider: { '@id': ORG_ID },
  };
  return [breadcrumbList(problemCrumbs(p)), resource];
}

/** /topics/{mavzu}: Bosh sahifa › Mavzular › {mavzu} */
export function topicStructuredData(topic: TopicMeta): JsonLdNode[] {
  return [breadcrumbList([HOME, TOPICS_CRUMB, { name: topic.title.uz, path: topicPath(topic.slug) }])];
}

/** /problems/difficulty/{level}: Bosh sahifa › Masalalar › {daraja} */
export function difficultyStructuredData(name: string, path: string): JsonLdNode[] {
  return [breadcrumbList([HOME, PROBLEMS_CRUMB, { name, path }])];
}

/** /topics: Bosh sahifa › Mavzular */
export function topicsIndexStructuredData(): JsonLdNode[] {
  return [breadcrumbList([HOME, TOPICS_CRUMB])];
}

/** /courses: Bosh sahifa › Kurslar */
export function coursesIndexStructuredData(): JsonLdNode[] {
  return [breadcrumbList([HOME, COURSES_CRUMB])];
}

/** Soniyalar → ISO 8601 davomiylik (PT1H5M) */
function isoDuration(totalSec: number): string {
  const min = Math.max(1, Math.round(totalSec / 60));
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}`;
}

/** /courses/{kurs}: BreadcrumbList + Course */
export function courseStructuredData(c: CourseDetailResponse): JsonLdNode[] {
  const url = absoluteUrl(coursePath(c.slug));
  const course: JsonLdNode = {
    '@type': 'Course',
    name: c.title,
    description: courseSeoDescription(c),
    url,
    inLanguage: 'uz',
    educationalLevel: COURSE_LEVEL_UZ[c.level],
    isAccessibleForFree: c.freeLessonCount === c.lessonCount,
    provider: { '@id': ORG_ID },
    // Narx bo'lsa — pullik kurs (bir martalik to'lov, umrbod kirish)
    ...(c.priceUzs != null
      ? { offers: { '@type': 'Offer', category: 'Paid', price: c.priceUzs, priceCurrency: 'UZS' } }
      : {}),
    ...(c.totalDurationSec > 0
      ? {
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'online',
            courseWorkload: isoDuration(c.totalDurationSec),
          },
        }
      : {}),
  };
  return [breadcrumbList([HOME, COURSES_CRUMB, { name: c.title, path: coursePath(c.slug) }]), course];
}

/**
 * /courses/{kurs}/{dars}: BreadcrumbList + LearningResource (Lesson). Qulflangan darsda
 * video/konspekt HTML ga chiqmaydi — masala sahifasidagi kabi paywall markup.
 */
export function lessonStructuredData(l: LessonResponse): JsonLdNode[] {
  const url = absoluteUrl(lessonPath(l.courseSlug, l.slug));
  const resource: JsonLdNode = {
    '@type': 'LearningResource',
    name: l.title,
    description: lessonSeoDescription(l),
    url,
    inLanguage: 'uz',
    learningResourceType: 'Lesson',
    educationalLevel: COURSE_LEVEL_UZ[l.courseLevel],
    teaches: l.courseTitle,
    ...(l.durationSec ? { timeRequired: isoDuration(l.durationSec) } : {}),
    isPartOf: { '@type': 'Course', name: l.courseTitle, url: absoluteUrl(coursePath(l.courseSlug)) },
    isAccessibleForFree: !l.locked,
    ...(l.locked
      ? {
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: false,
            cssSelector: `.${PAYWALL_CLASS}`,
          },
        }
      : {}),
    provider: { '@id': ORG_ID },
  };
  return [
    breadcrumbList([
      HOME,
      COURSES_CRUMB,
      { name: l.courseTitle, path: coursePath(l.courseSlug) },
      { name: l.title, path: lessonPath(l.courseSlug, l.slug) },
    ]),
    resource,
  ];
}

/**
 * /careers/{kasb}: BreadcrumbList (Bosh sahifa › Kurslar › kasb) + EducationalOccupationalProgram.
 * hasCourse — faqat saytda mavjud kurslar ("Tez orada" qadamlar kirmaydi).
 */
export function careerStructuredData(c: CareerDetailResponse): JsonLdNode[] {
  const url = absoluteUrl(careerPath(c.slug));
  const courses = c.steps.flatMap((s) => (s.available && s.course ? [s.course] : []));
  const program: JsonLdNode = {
    '@type': 'EducationalOccupationalProgram',
    name: c.title,
    description: careerSeoDescription(c),
    url,
    inLanguage: 'uz',
    educationalProgramMode: 'online',
    provider: { '@id': ORG_ID },
    ...(c.totalDurationSec > 0 ? { timeToComplete: isoDuration(c.totalDurationSec) } : {}),
    ...(courses.length
      ? {
          hasCourse: courses.map((co) => ({
            '@type': 'Course',
            name: co.title,
            url: absoluteUrl(coursePath(co.slug)),
            provider: { '@id': ORG_ID },
          })),
        }
      : {}),
  };
  return [breadcrumbList([HOME, COURSES_CRUMB, { name: c.title, path: careerPath(c.slug) }]), program];
}

/** /learn: Bosh sahifa › O'rganish */
export function learnIndexStructuredData(): JsonLdNode[] {
  return [breadcrumbList([HOME, LEARN_CRUMB])];
}

/**
 * /learn/{post}: BreadcrumbList + Article. Pullik (qulflangan) postda matn HTML ga chiqmaydi —
 * masala sahifasidagi kabi paywall markup.
 */
export function learnPostStructuredData(p: LearnPostResponse): JsonLdNode[] {
  const url = absoluteUrl(learnPath(p.slug));
  const topic = p.topic ? topicBySlug(p.topic) : undefined;
  const article: JsonLdNode = {
    '@type': 'Article',
    headline: p.title,
    description: learnSeoDescription(p),
    url,
    mainEntityOfPage: url,
    inLanguage: 'uz',
    ...(p.publishedAt ? { datePublished: p.publishedAt } : {}),
    ...(p.updatedAt ? { dateModified: p.updatedAt } : {}),
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    image: absoluteUrl(site.logo),
    ...(topic ? { about: topic.title.uz } : {}),
    timeRequired: `PT${Math.max(1, p.readingMinutes)}M`,
    isAccessibleForFree: !p.locked,
    ...(p.locked
      ? {
          hasPart: {
            '@type': 'WebPageElement',
            isAccessibleForFree: false,
            cssSelector: `.${PAYWALL_CLASS}`,
          },
        }
      : {}),
  };
  return [breadcrumbList([HOME, LEARN_CRUMB, { name: p.title, path: learnPath(p.slug) }]), article];
}

/** Sahifadagi barcha tugunlar: sayt bo'yicha (Organization, WebSite) + sahifaga xos */
export function pageStructuredData(page?: JsonLdNode[]): JsonLdNode[] {
  return [organization(), website(), ...(page ?? [])];
}

/** `<script>` ichidagi obyekt (har biri mustaqil hujjat — o'z @context i bilan) */
export function jsonLdDocument(node: JsonLdNode): Record<string, unknown> {
  return { '@context': 'https://schema.org', ...node };
}
